/**
 * AppErrorBoundary — catches any unhandled render-time error in the
 * React tree and shows a clean fallback instead of an empty white page.
 *
 * Why this exists:
 *   On 2026-06-14 a backend route started returning a structured
 *   `{message, expires_at}` error detail that the frontend tried to
 *   render as a child. The whole /palgame page went blank with only a
 *   cryptic dev overlay. This boundary is the safety net so that class
 *   of bug never takes the page down again — the user sees an
 *   apologetic card with a retry / report button, and we get a console
 *   log + (when wired) a Sentry-style report.
 *
 * Boundary scope is the WHOLE app (mounted in `index.js` above `<App/>`).
 * For per-route or per-widget isolation we can add nested boundaries
 * later — start global, narrow as needed.
 */
import React from "react";

/** The actual class boundary. Function components can't catch errors. */
export class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    // React calls this during render after a thrown error so we can
    // swap to the fallback tree. Keep the error so we can show its
    // message in dev mode.
    return { error };
  }

  componentDidCatch(error, info) {
    // Hook for future remote logging (Sentry, LogRocket, custom
    // /api/errors). For now we just log to the console so devs and
    // power users with the console open can see the trace.
    // eslint-disable-next-line no-console
    console.error("[AppErrorBoundary] caught:", error, info?.componentStack);
    // Fire-and-forget crash report. Backend dedupes by user+stack
    // for 5 minutes so a reload-loop doesn't flood the table or
    // spam the Discord webhook.
    try {
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const body = JSON.stringify({
        message: error?.message || String(error),
        stack: (error?.stack || "").slice(0, 8000),
        component_stack: (info?.componentStack || "").slice(0, 8000),
        url: window.location.href,
        user_agent: navigator.userAgent,
        build: process.env.REACT_APP_BUILD || null,
      });
      // Use sendBeacon if available — survives the page unload that
      // can happen if the user instantly hits "Reload". Fetch fallback
      // for browsers without it / dev preview.
      const endpoint = `${base}/api/crash`;
      if (navigator.sendBeacon) {
        try {
          navigator.sendBeacon(endpoint, new Blob([body], { type: "application/json" }));
          return;
        } catch (_) { /* fall through to fetch */ }
      }
      // Best-effort — we explicitly ignore the result so a broken
      // backend can't take the error UI down with it.
      fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        credentials: "include",
        keepalive: true,
      }).catch(() => {});
    } catch (_) {
      // Reporting the crash must NEVER itself crash the boundary.
    }
  }

  reset = () => this.setState({ error: null });

  render() {
    if (!this.state.error) return this.props.children;

    const isDev = process.env.NODE_ENV !== "production";
    const message = this.state.error?.message || String(this.state.error);

    return (
      <div
        // Pure black-on-black dark card so the boundary feels native
        // to the rest of the site rather than a browser-default crash.
        className="min-h-screen flex items-center justify-center p-6 text-[var(--bone)]"
        style={{ background: "var(--bg)" }}
        data-testid="app-error-boundary"
      >
        <div className="glass-card max-w-lg w-full p-6 text-center">
          <div className="text-6xl mb-3" aria-hidden>💀</div>
          <h1 className="text-2xl glow-teal mb-2" style={{ fontFamily: "'Cinzel Decorative', serif" }}>
            Something Broke
          </h1>
          <p className="text-sm text-[var(--muted)] mb-5">
            Your last action triggered an error somewhere in the page.
            We&apos;ve logged it. Try refreshing — if it keeps happening,
            drop a screenshot in the support channel.
          </p>

          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="btn"
              data-testid="app-error-reload"
            >
              Reload page
            </button>
            <button
              type="button"
              onClick={this.reset}
              className="btn btn-ghost"
              data-testid="app-error-dismiss"
              title="Hide the error and try the same page again. The bug may recur."
            >
              Try again
            </button>
            <a
              href="/"
              className="btn btn-ghost"
              data-testid="app-error-home"
            >
              Home
            </a>
          </div>

          {isDev && (
            <pre
              className="mt-5 text-left text-[10px] text-red-300 bg-black/40 border border-red-400/30 rounded p-3 overflow-auto max-h-40"
              data-testid="app-error-dev-message"
            >
              {message}
            </pre>
          )}
        </div>
      </div>
    );
  }
}
